import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_BADGE = {
  requested: "secondary", accepted: "default", assigned: "default",
  in_progress: "default", completed: "default", declined: "destructive", cancelled: "outline",
};

export default function ClientServices() {
  const [catalog, setCatalog] = useState(null);
  const [requests, setRequests] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ key: "", name: "", description: "" });

  const load = () => {
    api.get("/services/catalog").then(({ data }) => setCatalog(data)).catch(() => setCatalog([]));
    api.get("/services/requests").then(({ data }) => setRequests(data)).catch(() => setRequests([]));
  };
  useEffect(() => { load(); }, []);

  const createService = async () => {
    if (!form.key.trim() || !form.name.trim()) { toast.error("Key and name are required"); return; }
    try {
      await api.post("/services/catalog", form);
      toast.success("Service added to catalog");
      setCreating(false); setForm({ key: "", name: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const updateRequestStatus = async (id, status) => {
    try { await api.patch(`/services/requests/${id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Client Services" subtitle="Non-insurance support offerings for your existing clients (transportation, forms help, community resources, etc.).">
        <Button size="sm" onClick={() => setCreating(true)} data-testid="add-service">
          <Plus className="mr-1 h-4 w-4" /> Add Service
        </Button>
      </PageHeader>

      <div>
        <h3 className="mb-2 font-heading text-sm font-semibold">Catalog</h3>
        <div className="flex flex-wrap gap-2">
          {(catalog || []).map((s) => (
            <Badge key={s.key} variant={s.active ? "default" : "outline"}>{s.name}</Badge>
          ))}
          {catalog && catalog.length === 0 && (
            <span className="text-sm text-muted-foreground">No services in the catalog yet.</span>
          )}
        </div>
      </div>

      <div>
        <h3 className="mb-2 font-heading text-sm font-semibold">Requests</h3>
        <div className="overflow-x-auto rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead className="text-right">Update</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(requests || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  {requests === null ? "Loading…" : "No service requests yet."}
                </TableCell></TableRow>
              )}
              {(requests || []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.client_id}</TableCell>
                  <TableCell>{r.service_name}</TableCell>
                  <TableCell><Badge variant={STATUS_BADGE[r.status] || "secondary"}>{r.status}</Badge></TableCell>
                  <TableCell>{r.assigned_to || "—"}</TableCell>
                  <TableCell>{r.requested_at ? new Date(r.requested_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Select value={r.status} onValueChange={(v) => updateRequestStatus(r.id, v)}>
                      <SelectTrigger className="ml-auto w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["requested", "accepted", "assigned", "in_progress", "completed", "declined", "cancelled"].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Service to Catalog</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Key</Label>
              <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="ride_assistance" />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ride Assistance" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createService}><Loader2 className="hidden h-4 w-4 animate-spin" />Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
